import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import {
  PIP_CATEGORIES,
  PIP_SPAWN_CHANCE,
  PIP_VISIBLE_MS,
  isPipSpawnRoute,
  matchesRoute,
} from "../../constants/pipConfig";
import {
  awardPipFind,
  checkPipCooldown,
  dispatchPipEvent,
} from "../../services/pipService";

const PipContext = createContext(null);

function pickRandom(categories) {
  if (!categories.length) return null;
  return categories[Math.floor(Math.random() * categories.length)];
}

function isGamesPath(pathname = "") {
  return pathname === "/games" || pathname.startsWith("/games/");
}

/**
 * Easter-egg Pip: ~1/40 on feed / gallery / perks.
 * Games page: Pip stays permanently until the user clicks to hide him.
 */
export function PipProvider({ children }) {
  const { currentUser, userPerks, refreshUserPerks } = useAuth();
  const location = useLocation();

  const anchorsRef = useRef(new Map());
  const anchorListenersRef = useRef(new Set());
  const rolledPathRef = useRef("");
  const gamesHiddenRef = useRef(false);
  const hideTimerRef = useRef(null);

  const [activeSpawn, setActiveSpawn] = useState(null);
  const [foundState, setFoundState] = useState(false);
  const [toast, setToast] = useState(null);

  const pathname = location.pathname;

  const cooldown = useMemo(
    () => checkPipCooldown(userPerks),
    [userPerks]
  );

  const outfit = userPerks?.pipOutfit || null;

  const notifyAnchorListeners = useCallback(() => {
    anchorListenersRef.current.forEach((fn) => fn());
  }, []);

  const registerAnchor = useCallback((anchorId, rect) => {
    if (!rect) return;
    const next = {
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
      height: rect.height,
    };
    const prev = anchorsRef.current.get(anchorId);
    if (
      prev &&
      Math.abs(prev.top - next.top) < 1 &&
      Math.abs(prev.left - next.left) < 1 &&
      Math.abs(prev.width - next.width) < 1 &&
      Math.abs(prev.height - next.height) < 1
    ) {
      return;
    }
    anchorsRef.current.set(anchorId, next);
    notifyAnchorListeners();
  }, [notifyAnchorListeners]);

  const unregisterAnchor = useCallback((anchorId) => {
    if (!anchorsRef.current.has(anchorId)) return;
    anchorsRef.current.delete(anchorId);
    notifyAnchorListeners();
  }, [notifyAnchorListeners]);

  const getAnchorRect = useCallback((anchorId) => {
    const stored = anchorsRef.current.get(anchorId);
    if (!stored) return null;
    return {
      top: stored.top - window.scrollY,
      left: stored.left - window.scrollX,
      width: stored.width,
      height: stored.height,
    };
  }, []);

  const subscribeAnchors = useCallback((fn) => {
    anchorListenersRef.current.add(fn);
    return () => {
      anchorListenersRef.current.delete(fn);
    };
  }, []);

  const clearSpawn = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    setActiveSpawn(null);
    setFoundState(false);
  }, []);

  const showToast = useCallback((message) => {
    setToast(message);
    setTimeout(() => setToast(null), 3500);
  }, []);

  const hideGamesPip = useCallback(() => {
    gamesHiddenRef.current = true;
    clearSpawn();
  }, [clearSpawn]);

  const handleFound = useCallback(async (categoryId) => {
    if (!currentUser || foundState) return;
    if (cooldown.onCooldown) return;

    setFoundState(true);
    try {
      const result = await awardPipFind(categoryId || "CAT_RANDOM");
      if (refreshUserPerks) await refreshUserPerks(currentUser.uid);
      showToast(`✦ You found Pip! +${result.credits || 50} credits`);
      if (result.pipMaster) {
        setTimeout(() => showToast("👑 Pip Master! You found every hiding spot!"), 3600);
      }
    } catch (err) {
      showToast(err.message || "Could not award Pip credits.");
    }
    setTimeout(() => clearSpawn(), 2000);
  }, [currentUser, foundState, cooldown.onCooldown, refreshUserPerks, showToast, clearSpawn]);

  const spawnCategory = useCallback((category, opts = {}) => {
    if (!currentUser || !category) return;
    const alwaysVisible = Boolean(opts.alwaysVisible || category.spawnType === "always");
    if (!opts.bypassCooldown && cooldown.onCooldown && !alwaysVisible) return;

    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    const position = opts.position || {
      bottom: alwaysVisible ? "120px" : "100px",
      right: "20px",
      anchor: false,
    };

    setActiveSpawn({
      categoryId: category.id,
      state: category.state || "idle",
      peek: category.peek || "A",
      speech: category.speech || null,
      position,
      duration: alwaysVisible ? null : PIP_VISIBLE_MS,
      alwaysVisible,
      isGames: alwaysVisible && category.id === "CAT_022",
    });
    setFoundState(false);

    if (!alwaysVisible) {
      hideTimerRef.current = setTimeout(() => clearSpawn(), PIP_VISIBLE_MS);
    }
  }, [currentUser, cooldown.onCooldown, clearSpawn]);

  const evaluateAndSpawn = useCallback(() => {
    clearSpawn();

    if (!currentUser) return;

    // Games page renders Pip inline next to the headline — no floating egg.
    if (isGamesPath(pathname)) {
      return;
    }

    if (!isPipSpawnRoute(pathname)) return;
    if (cooldown.onCooldown) return;

    // One roll per path visit — do not re-roll on re-renders.
    if (rolledPathRef.current === pathname) return;
    rolledPathRef.current = pathname;

    // ~1/40 easter-egg chance
    if (Math.random() > PIP_SPAWN_CHANCE) return;

    const eligible = PIP_CATEGORIES.filter((cat) => {
      if (cat.spawnType !== "random") return false;
      if (cat.routes?.some((r) => r === "/drop" || r.startsWith("/drop"))) return false;
      if (cat.routes?.length && !matchesRoute(cat, pathname)) return false;
      return true;
    });

    const chosen = pickRandom(eligible);
    if (!chosen) {
      spawnCategory({ id: "CAT_RANDOM", state: "idle", peek: "A" });
      return;
    }

    spawnCategory(chosen);
  }, [currentUser, pathname, cooldown.onCooldown, clearSpawn, spawnCategory]);

  useEffect(() => {
    evaluateAndSpawn();
  }, [pathname, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving games resets the "hidden" flag so Pip returns next visit.
  useEffect(() => {
    if (!isGamesPath(pathname)) {
      gamesHiddenRef.current = false;
    }
  }, [pathname]);

  // Clear random Pip when leaving spawn-eligible pages (never force-clear games here —
  // evaluateAndSpawn owns games visibility).
  useEffect(() => {
    if (!isPipSpawnRoute(pathname) && !isGamesPath(pathname)) {
      clearSpawn();
    }
  }, [pathname, clearSpawn]);

  const value = useMemo(() => ({
    activeSpawn,
    foundState,
    toast,
    cooldown,
    outfit,
    registerAnchor,
    unregisterAnchor,
    getAnchorRect,
    subscribeAnchors,
    handleFound,
    hideGamesPip,
    clearSpawn,
    spawnCategory,
    setContextData: () => {},
    showToast,
    dispatchPipEvent,
  }), [
    activeSpawn,
    foundState,
    toast,
    cooldown,
    outfit,
    registerAnchor,
    unregisterAnchor,
    getAnchorRect,
    subscribeAnchors,
    handleFound,
    hideGamesPip,
    clearSpawn,
    spawnCategory,
    showToast,
  ]);

  return (
    <PipContext.Provider value={value}>
      {children}
    </PipContext.Provider>
  );
}

export function usePip() {
  const ctx = useContext(PipContext);
  if (!ctx) throw new Error("usePip must be used within PipProvider");
  return ctx;
}

export function usePipOptional() {
  return useContext(PipContext);
}

export function usePipAnchorRect(anchorId) {
  const pip = usePipOptional();
  const getAnchorRect = pip?.getAnchorRect;
  const subscribeAnchors = pip?.subscribeAnchors;
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!getAnchorRect || !subscribeAnchors || !anchorId) {
      setRect(null);
      return undefined;
    }
    const read = () => setRect(getAnchorRect(anchorId));
    read();
    return subscribeAnchors(read);
  }, [getAnchorRect, subscribeAnchors, anchorId]);

  return rect;
}
